// ═══ 업무별 기록 v5 — 장부 반영 / 반영 취소 확인 창 (DESIGN_V5 §2.7·§2.8·§6.2·§6.3) ═══
// 실행 전에 업체별로 장부에 들어갈(빠질) 금액을 표로 보여 주고, 확인 버튼 한 번으로 실행한다.
// 서버 계약:
//   GET  /work-records/cards/:balanceId/preview → {success, preview:{byUnit, totals, dateReplaced, overlap, carryGap, v2Active, affectedStatements, byUnitHash}}
//   POST /work-records/cards/:balanceId/post {memo, gapChoice?, gapDeciderName?, expect:{items,payments,adjusts,net,byUnitHash}}
//        → {success, runId, posted:{items,charges,payments,adjusts}, decisionId, affectedStatements}
//   GET  /work-records/runs/:runId/undo-preview → {success, preview:{byUnit, detached, decisions, affectedStatements, byUnitHash}}
//   POST /work-records/runs/:runId/undo {memo, expect:{byUnitHash}} → {success, result:{removed, reversed, detached, decisionsReversed}}
// carry 카드(carryGap 있음)는 이월 안 된 금액 결정(엑셀대로/그대로)과 확인자 이름이 있어야 실행된다 — 반영과 같은 trx.
// 409 PREVIEW_CHANGED → 미리보기를 다시 불러와 보여 주고 다시 누르게 한다 (post·undo 는 본문에 전체 preview 가 오지만 GET 재조회로 통일).
//
// props: mode('post'|'undo') · card · userName · showToast · onClose · onDone · onOverlap(겹침 정하기로 이동)
import React, { useState, useEffect } from 'react';
import { API, authFetch } from '../utils/api';
import { is } from './SettlementItemDrawer';

var MEMO_MAX = 200;
var NAME_MAX = 50;
export var num = function (n) { return parseInt(n, 10) || 0; };
export var fmt = function (n) { return num(n).toLocaleString(); };
export var signed = function (n) { var v = num(n); return (v > 0 ? '+' : (v < 0 ? '−' : '')) + fmt(Math.abs(v)); };
export var signColor = function (n) { var v = num(n); return v > 0 ? '#dc2626' : (v < 0 ? '#059669' : '#64748b'); };
export var postJson = function (url, body) {
  return authFetch(API + url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
    .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { status: r.status, data: d || {} }; }); });
};
export var getJson = function (url) {
  return authFetch(API + url).then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return { status: r.status, data: d || {} }; }); });
};
export var overlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 };
export var box = { background: '#fff', borderRadius: 12, padding: 22, width: 720, maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto', fontFamily: 'Pretendard,sans-serif' };
export var th = { padding: '8px', fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', textAlign: 'left', color: '#475569', background: '#f8fafc' };
export var thR = Object.assign({}, th, { textAlign: 'right' });
export var td = { padding: '8px', fontSize: 13.5, whiteSpace: 'nowrap', borderTop: '1px solid #f1f5f9' };
export var tdR = Object.assign({}, td, { textAlign: 'right' });
export var noteBox = { marginTop: 10, padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13.5, color: '#92400e', lineHeight: 1.6 };
export var errBoxSt = { marginTop: 10, padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13.5, color: '#b91c1c', lineHeight: 1.6 };
export var CHOICE_LABEL = { excel: '엑셀대로 정리', keep: '장부대로 둠', post: '장부에 반영' };
export var DETACH_LABEL = { deleted: '장부에서 지움', edited: '장부에서 고침', client_changed: '업체를 바꿈', reversed: '입금을 고침' };
export var KIND_LABEL = { item: '건', payment: '입금', adjust: '조정' };
var unitLabel = function (u) { return (u.unitName || '-') + (u.accountName && u.accountName !== u.unitName ? ' · ' + u.accountName : ''); };

// 409 코드 → 쉬운 안내 문구
export function codeMessage(code, d) {
  d = d || {};
  if (code === 'PREVIEW_CHANGED') return '그 사이 기록이 바뀌었습니다. 새 내용을 다시 보여 드립니다. 확인한 뒤 다시 눌러 주세요.';
  if (code === 'ALREADY_POSTED') return '이미 장부에 반영된 묶음입니다. 화면을 새로 불러왔습니다.';
  if (code === 'NOT_POSTED') return '이미 취소된 반영입니다. 화면을 새로 불러왔습니다.';
  if (code === 'V2_ACTIVE') return '예전 방식 \'미수 맞춤\' 반영이 남아 있습니다. 카드에서 먼저 취소해 주세요.';
  if (code === 'UNLINKED_REMAIN') { var u = d.unlinked || {}; return '아직 업체에 연결하지 않은 기록이 ' + fmt(num(u.items) + num(u.deposits) + num(u.adjusts)) + '건 있습니다. 먼저 업체를 연결해 주세요.'; }
  if (code === 'INELIGIBLE_UNITS') return '사용하지 않는 업체에 연결된 기록이 있습니다: ' + (d.units || []).map(function (x) { return x.unitName || x.unitId; }).join(', ') + '. 사용 중인 업체로 다시 연결해 주세요.';
  if (code === 'SCOPE_MISMATCH') return '엑셀 합계와 기록 합계가 맞지 않아 반영할 수 없습니다 (엑셀 ' + fmt(d.expected) + '원 · 기록 ' + fmt(d.actual) + '원). 관리자에게 알려 주세요.';
  if (code === 'ERP_OVERLAP') return 'ERP 에 이미 있는 건을 먼저 정해 주세요. 카드의 [겹친 건 정하기]를 눌러 주세요.';
  if (code === 'GAP_CHOICE_REQUIRED') return '이월 안 된 금액을 어떻게 할지 골라 주세요.';
  if (code === 'CARD_POSTED') return '이미 장부에 반영된 묶음이라 바꿀 수 없습니다. 반영을 취소한 뒤 다시 해 주세요.';
  if (code === 'CARD_NOT_POSTED') return '이 묶음을 먼저 장부에 반영해 주세요.';
  if (code === 'DECIDE_WITH_POST') return '이 항목은 묶음을 장부에 반영할 때 함께 정합니다.';
  if (code === 'ALREADY_DECIDED') return '이미 다른 결정이 있습니다. 화면을 새로 불러왔습니다.';
  if (code === 'CARRY_GAP_LOCKED') return '이월 항목은 따로 취소할 수 없습니다. 묶음 반영을 취소하면 함께 취소됩니다.';
  if (code === 'NOT_DECIDED') return '아직 결정하지 않은 항목입니다. 화면을 새로 불러왔습니다.';
  if (code === 'NO_DRIFT') return '장부와 결정이 이미 맞습니다. 다시 맞출 것이 없습니다.';
  if (code === 'CARD_GAP_MISSING') return '이 묶음에 필요한 확인 항목(' + (d.carryGapNo || '') + ') 자료가 없어 반영할 수 없습니다. 직접 고칠 수 없는 자료 문제이니 관리자에게 알려 주세요.';
  if (code === 'SAME_CHOICE') return '이미 같은 결정이 되어 있어 그대로 다시 할 수 없습니다. [결정 취소]를 누른 뒤 다시 정해 주세요.';
  return d.error || '처리하지 못했습니다';
}

// 업체별 반영 표 (§6.2 preview.byUnit) — 결정 미리보기의 G4 'post' 에서도 쓴다
export function PostUnitTable(props) {
  var rows = props.rows || [], t = props.totals;
  var sum = function (k) { return rows.reduce(function (s, u) { return s + num(u[k]); }, 0); };
  return (<div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
    <table data-testid="post-units" style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse' }}>
      <thead><tr><th style={th}>업체</th><th style={thR}>건</th><th style={thR}>청구</th><th style={thR}>입금</th><th style={thR}>조정</th><th style={thR}>미수에 더해짐</th><th style={th}>들어가는 달</th></tr></thead>
      <tbody>
        {rows.map(function (u) {
          var m = u.months || [];
          return (<tr key={u.unitId} data-testid="post-unit">
            <td style={Object.assign({}, td, { fontWeight: 700, color: '#1B3A5C' })}>{unitLabel(u)}</td>
            <td style={tdR}>{fmt(u.items)}</td>
            <td style={tdR}>{fmt(u.charged)}</td>
            <td style={tdR}>{fmt(u.paid)}{num(u.payments) ? <span style={{ color: '#94a3b8', fontSize: 12 }}> ({fmt(u.payments)}건)</span> : null}</td>
            <td style={tdR}>{num(u.adjusts) ? fmt(u.adjusted) : '-'}</td>
            <td style={Object.assign({}, tdR, { fontWeight: 800, color: signColor(u.net) })}>{signed(u.net)}</td>
            <td style={Object.assign({}, td, { color: '#64748b' })}>{m.length ? (m[0] === m[m.length - 1] ? m[0] : m[0] + ' ~ ' + m[m.length - 1]) : '-'}</td>
          </tr>);
        })}
        {!!t && <tr style={{ background: '#f8fafc' }}>
          <td style={Object.assign({}, td, { fontWeight: 800 })}>합계 {rows.length}곳</td>
          <td style={tdR}>{fmt(t.items)}</td><td style={tdR}>{fmt(sum('charged'))}</td>
          <td style={tdR}>{fmt(sum('paid'))}{num(t.payments) ? <span style={{ color: '#94a3b8', fontSize: 12 }}> ({fmt(t.payments)}건)</span> : null}</td>
          <td style={tdR}>{num(t.adjusts) ? fmt(sum('adjusted')) : '-'}</td>
          <td style={Object.assign({}, tdR, { fontWeight: 800, color: signColor(t.net) })}>{signed(t.net)}</td><td style={td} />
        </tr>}
      </tbody>
    </table>
  </div>);
}

// 정리 줄 표 — [{unitId, unitName, amount}]
export function LinesTable(props) {
  var lines = props.lines || [];
  if (!lines.length) return <div style={{ fontSize: 13.5, color: '#64748b' }}>{props.empty || '장부에 넣을 정리 줄이 없습니다.'}</div>;
  return (<table data-testid="gap-lines" style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0' }}>
    <thead><tr><th style={th}>업체</th><th style={thR}>{props.amountLabel || '2026년 8월 정리 금액'}</th></tr></thead>
    <tbody>{lines.map(function (l, i) {
      return <tr key={i}><td style={td}>{l.unitName || l.unitId}</td><td style={Object.assign({}, tdR, { fontWeight: 700, color: signColor(l.amount) })}>{signed(l.amount)}</td></tr>;
    })}</tbody>
  </table>);
}

// 장부에서 고친(분리된) 기록 목록
export function DetachedList(props) {
  var rows = props.rows || [];
  if (!rows.length) return null;
  return (<div style={noteBox}>
    <b>{props.title || '장부에서 직접 고친 기록'} {fmt(rows.length)}건</b> — {props.note || '고친 내용은 일반 장부 줄로 그대로 남습니다.'}
    <div style={{ marginTop: 4, maxHeight: 140, overflowY: 'auto' }}>{rows.map(function (r, i) {
      return <div key={i}>{r.srcRow}행 · {KIND_LABEL[r.kind] || r.kind} · {DETACH_LABEL[r.reason] || r.reason} · 차이 {signed(r.delta)}원</div>;
    })}</div>
  </div>);
}

export function Statements(props) {
  var list = props.list || [];
  if (!list.length) return null;
  return (<div style={noteBox} data-testid="affected-statements">
    <b>다시 발행해야 할 정산서 {list.length}건</b> — 반영 뒤 전월 미수가 바뀝니다.
    <div>{list.map(function (s) { return <div key={s.statementId}>{s.accountName} · {s.period}</div>; })}</div>
  </div>);
}

export default function RecordsPreviewModal(props) {
  var mode = props.mode, card = props.card || {};
  var isPost = mode === 'post';
  var showToast = props.showToast || function () {};
  var [pv, setPv] = useState(null);
  var [loadErr, setLoadErr] = useState('');
  var [reloadN, setReloadN] = useState(0);
  var [notice, setNotice] = useState('');
  var [choice, setChoice] = useState('');
  var [decider, setDecider] = useState(props.userName || '');
  var [memo, setMemo] = useState('');
  var [busy, setBusy] = useState(false);
  var url = isPost ? '/work-records/cards/' + card.balanceId : '/work-records/runs/' + (card.run && card.run.runId);

  useEffect(function () {
    var alive = true;
    setPv(null); setLoadErr('');
    getJson(url + (isPost ? '/preview' : '/undo-preview')).then(function (res) {
      if (!alive) return;
      if (res.data.success && res.data.preview) setPv(res.data.preview);
      else setLoadErr(res.data.code ? codeMessage(res.data.code, res.data) : (res.data.error || '미리보기를 만들지 못했습니다'));
    }).catch(function () { if (alive) setLoadErr('서버 오류'); });
    return function () { alive = false; };
  }, [url, isPost, reloadN]);

  var p = pv || {};
  var carry = isPost && !!p.carryGap;
  var pendingOverlap = (p.overlap || []).filter(function (o) { return !o.choice; });
  var v2 = p.v2Active || [];
  var memoOk = isPost ? memo.trim().length <= MEMO_MAX : (memo.trim().length > 0 && memo.trim().length <= MEMO_MAX);
  var carryOk = !carry || (!!choice && decider.trim().length > 0 && decider.trim().length <= NAME_MAX);
  var canRun = !!pv && !busy && memoOk && carryOk && (!isPost || (!pendingOverlap.length && !v2.length));

  var run = function () {
    if (!canRun) return;
    var body;
    if (isPost) {
      var t = p.totals || {};
      body = { memo: memo.trim(), expect: { items: t.items, payments: t.payments, adjusts: t.adjusts, net: t.net, byUnitHash: p.byUnitHash } };
      if (carry) { body.gapChoice = choice; body.gapDeciderName = decider.trim(); }
    } else {
      body = { memo: memo.trim(), expect: { byUnitHash: p.byUnitHash } };
    }
    setBusy(true); setNotice('');
    postJson(url + (isPost ? '/post' : '/undo'), body).then(function (res) {
      setBusy(false);
      var d = res.data;
      if (d.success) {
        if (isPost) showToast(card.no + ' 장부에 반영했습니다 — 건 ' + fmt(d.posted && d.posted.items) + ' · 입금 ' + fmt(d.posted && d.posted.payments));
        else showToast(card.no + ' 반영을 취소했습니다 — 지운 건 ' + fmt(d.result && d.result.removed) + ' · 되돌린 줄 ' + fmt(d.result && d.result.reversed));
        props.onDone(); return;
      }
      if (d.code === 'PREVIEW_CHANGED') { setNotice(codeMessage(d.code, d)); setReloadN(function (n) { return n + 1; }); return; }
      if (d.code === 'ALREADY_POSTED' || d.code === 'NOT_POSTED') { showToast(codeMessage(d.code, d), 'err'); props.onDone(); return; }
      setNotice(codeMessage(d.code, d));
    }).catch(function () { setBusy(false); setNotice('서버 오류 — 잠시 뒤 다시 눌러 주세요.'); });
  };

  var choiceBtn = function (key, title, desc) {
    var on = choice === key;
    return (<button type="button" role="radio" aria-checked={on} aria-label={title} onClick={function () { setChoice(key); }}
      style={{ flex: 1, minWidth: 220, textAlign: 'left', padding: '12px 14px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit',
        border: '2px solid ' + (on ? '#2563EB' : '#e2e8f0'), background: on ? '#eff6ff' : '#fff' }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: on ? '#1d4ed8' : '#334155' }}>{on ? '● ' : '○ '}{title}</div>
      <div style={{ fontSize: 13, color: '#64748b', marginTop: 4, lineHeight: 1.5 }}>{desc}</div>
    </button>);
  };

  var title = card.no + ' ' + (card.label || '') + (isPost ? ' — 장부에 반영' : ' — 반영 취소');
  return (<div style={overlay} onClick={function () { if (!busy) props.onClose(); }}>
    <div role="dialog" aria-label={title} onClick={function (e) { e.stopPropagation(); }} style={box}>
      <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: '#1B3A5C' }}>{title}</h3>
      <p style={{ margin: '0 0 12px', fontSize: 14, color: '#475569', lineHeight: 1.6 }}>{isPost
        ? '아래 금액이 업체별 장부에 엑셀의 원래 날짜로 들어갑니다. 확인한 뒤 [장부에 반영]을 눌러 주세요.'
        : '이 묶음으로 장부에 넣었던 금액을 업체별로 뺍니다. 확인한 뒤 취소 이유를 적고 [반영 취소]를 눌러 주세요.'}</p>
      {!!loadErr && <div style={errBoxSt}>{loadErr}</div>}
      {!pv && !loadErr && <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>미리보기 만드는 중…</div>}
      {!!notice && <div style={noteBox} data-testid="preview-notice">{notice}</div>}

      {!!pv && isPost && <div>
        <PostUnitTable rows={p.byUnit} totals={p.totals} />
        {(p.dateReplaced || []).length > 0 && <details style={{ marginTop: 10, fontSize: 13.5, color: '#475569' }}>
          <summary style={{ cursor: 'pointer' }}>날짜를 바꿔 넣는 기록 {fmt(p.dateReplaced.length)}건 (엑셀 날짜가 비었거나 이상함)</summary>
          {p.dateReplaced.map(function (r) { return <div key={r.recordId}>{r.srcRow}행 · 엑셀 '{r.from || '빈칸'}' → {r.to}</div>; })}
        </details>}
        {(p.overlap || []).length > 0 && <div style={noteBox} data-testid="preview-overlap">
          ERP 에 이미 있는 건 {fmt(p.overlap.length)}건{pendingOverlap.length ? ' — 아직 정하지 않은 ' + fmt(pendingOverlap.length) + '건이 있어 반영할 수 없습니다.' : ' — 모두 정했습니다.'}
          {pendingOverlap.length > 0 && !!props.onOverlap && <div style={{ marginTop: 6 }}>
            <button className="btn btn-blue btn-sm" onClick={props.onOverlap}>겹친 건 정하기</button></div>}
        </div>}
        {num(p.reentered && p.reentered.count) > 0 && <div style={noteBox} data-testid="preview-reentered">
          장부에서 입금을 고쳐 이 묶음 밖에 다시 넣은 입금 {fmt(p.reentered.count)}건 {fmt(p.reentered.amount)}원이 있습니다. 그만큼 엑셀과 차이가 납니다.</div>}
        {v2.length > 0 && <div style={errBoxSt}>예전 방식 '미수 맞춤' 반영 {v2.length}건({v2.map(function (v) { return fmt(v.amount) + '원'; }).join(', ')})을 카드에서 먼저 취소해 주세요.</div>}
        <Statements list={p.affectedStatements} />
        {carry && <div data-testid="carry-choice" style={{ marginTop: 14, padding: 14, border: '2px solid #fde68a', borderRadius: 10, background: '#fffdf5' }}>
          <div style={{ fontSize: 15.5, fontWeight: 800, color: '#92400e', marginBottom: 4 }}>{p.carryGap.no} 이월 안 된 금액 — 꼭 골라 주세요</div>
          <div style={{ fontSize: 13.5, color: '#64748b', marginBottom: 10, lineHeight: 1.6 }}>엑셀에서 다음 묶음으로 넘어가지 않은 미수입니다. 반영과 함께 정해집니다.</div>
          <div role="radiogroup" aria-label="이월 안 된 금액" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {choiceBtn('excel', CHOICE_LABEL.excel, '2026년 8월 장부에 아래 정리 줄을 넣어 엑셀 미수와 맞춥니다.')}
            {choiceBtn('keep', CHOICE_LABEL.keep, '정리 줄 없이 반영한 금액 그대로 둡니다.')}
          </div>
          {choice === 'excel' && <div style={{ marginTop: 10 }}><LinesTable lines={p.carryGap.excelLines} /></div>}
          <label style={{ display: 'block', marginTop: 12, fontSize: 13.5, fontWeight: 700, color: '#475569' }}>확인한 사람 이름
            <input aria-label="확인한 사람 이름" value={decider} maxLength={NAME_MAX} onChange={function (e) { setDecider(e.target.value); }}
              style={Object.assign({}, is, { marginTop: 4, fontSize: 14 })} />
          </label>
        </div>}
      </div>}

      {!!pv && !isPost && <div>
        <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
          <table data-testid="undo-units" style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>업체</th><th style={thR}>빠지는 청구</th><th style={thR}>되돌리는 입금</th><th style={thR}>되돌리는 조정</th><th style={thR}>미수 변화</th></tr></thead>
            <tbody>{(p.byUnit || []).map(function (u) {
              return (<tr key={u.unitId} data-testid="undo-unit"><td style={Object.assign({}, td, { fontWeight: 700, color: '#1B3A5C' })}>{u.unitName || '-'}</td>
                <td style={tdR}>{fmt(u.removeCharged)}</td><td style={tdR}>{fmt(u.reversePaid)}</td><td style={tdR}>{num(u.reverseAdjust) ? fmt(u.reverseAdjust) : '-'}</td>
                <td style={Object.assign({}, tdR, { fontWeight: 800, color: signColor(u.net) })}>{signed(u.net)}</td></tr>);
            })}</tbody>
          </table>
        </div>
        <DetachedList rows={p.detached} note="고친 내용은 일반 장부 줄로 남고 이번 취소에서 빠집니다." />
        {(p.decisions || []).length > 0 && <div style={noteBox}>
          <b>함께 취소되는 확인 항목 결정 {p.decisions.length}건</b>
          {p.decisions.map(function (g) {
            return (<div key={g.gapId} style={{ marginTop: 6 }}>{g.no} · {CHOICE_LABEL[g.choice] || g.choice}
              {(g.lines || []).length > 0 && <span> · 정리 줄 {g.lines.map(function (l) { return (l.unitName || l.unitId) + ' ' + signed(l.amount); }).join(', ')} 를 되돌립니다</span>}</div>);
          })}
        </div>}
        <Statements list={p.affectedStatements} />
      </div>}

      {!!pv && <label style={{ display: 'block', marginTop: 14, fontSize: 13.5, fontWeight: 700, color: '#475569' }}>{isPost ? '메모 (적지 않아도 됩니다)' : '취소 이유 (꼭 적어 주세요)'}
        <input aria-label={isPost ? '메모' : '취소 이유'} value={memo} maxLength={MEMO_MAX} onChange={function (e) { setMemo(e.target.value); }}
          style={Object.assign({}, is, { marginTop: 4, fontSize: 14 })} />
      </label>}
      {!!pv && carry && !choice && <div style={{ marginTop: 8, fontSize: 13.5, color: '#b45309' }}>이월 안 된 금액을 어떻게 할지 먼저 골라 주세요.</div>}

      <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
        <button className="btn btn-outline" style={{ flex: 1, justifyContent: 'center', minHeight: 44, fontSize: 15 }} disabled={busy} onClick={props.onClose}>닫기</button>
        <button className="btn btn-blue" style={Object.assign({ flex: 1, justifyContent: 'center', minHeight: 44, fontSize: 15 }, canRun ? null : { opacity: 0.45, cursor: 'not-allowed' })}
          disabled={!canRun} onClick={run}>{busy ? '처리 중…' : (isPost ? '장부에 반영' : '반영 취소')}</button>
      </div>
    </div>
  </div>);
}
